import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import * as recsApi from '../api/recommendations';
import styles from './Recommendations.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
};

// Извлекает короткий тип файла из MIME или имени
const getFileExt = (rec) => {
  if (rec.file_name) {
    const m = rec.file_name.match(/\.([a-z0-9]+)$/i);
    if (m) return m[1].toUpperCase();
  }
  return 'ФАЙЛ';
};

const RecommendationCard = ({ rec, isPaid }) => {
  const isBlurred = rec.blurred;

  return (
    <article className={`${styles.card} ${isBlurred ? styles.cardLocked : ''}`}>
      {rec.image_url && (
        <div className={styles.imageWrap}>
          <img
            src={`${API_URL}${rec.image_url}`}
            alt={rec.image_alt || rec.title}
            className={styles.image}
          />
          {isBlurred && (
            <div className={styles.imageLockBadge}>
              <span className={styles.lockIcon}>🔒</span>
              <span>Только для участников</span>
            </div>
          )}
        </div>
      )}

      <div className={styles.cardBody}>
        <div className={styles.cardTitleRow}>
          <h3 className={styles.cardTitle}>{rec.title}</h3>
          {rec.visible_to_paid_only && (
            <span className={styles.paidBadge}>Premium</span>
          )}
        </div>

        {isBlurred ? (
          <div className={styles.blurredContent}>
            <p className={styles.blurredText}>
              Оформите подписку, чтобы видеть полное описание, промокоды и ссылки на источник.
            </p>
            <Link to="/payment" className={styles.upgradeBtn}>
              Оформить подписку — 2 000 ₽/мес
            </Link>
          </div>
        ) : (
          <>
            <p className={styles.description}>{rec.description}</p>

            {rec.promo_code && (
              <div className={styles.promoBlock}>
                <span className={styles.promoLabel}>Промокод:</span>
                <span className={styles.promoCode}>{rec.promo_code}</span>
              </div>
            )}

            {rec.link && (
              <a
                href={rec.link}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.sourceLink}
              >
                Подробнее →
              </a>
            )}

            {rec.file_url && (
              <a
                href={`${API_URL}${rec.file_url}`}
                download={rec.file_name || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.fileLink}
              >
                <span className={styles.fileIcon}>📄</span>
                <span className={styles.fileMeta}>
                  <span className={styles.fileName}>
                    {rec.file_name || 'Скачать файл'}
                  </span>
                  <span className={styles.fileSize}>
                    {getFileExt(rec)}{rec.file_size ? ` · ${formatFileSize(rec.file_size)}` : ''}
                  </span>
                </span>
              </a>
            )}
          </>
        )}
      </div>
    </article>
  );
};

const Recommendations = () => {
  const { token, user } = useAuth();
  const [recs, setRecs] = useState([]);
  const [isPaid, setIsPaid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showPaidOnly, setShowPaidOnly] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await recsApi.getRecommendations(token);
        setRecs(data.recommendations);
        setIsPaid(data.is_paid);
      } catch {
        setError('Не удалось загрузить рекомендации');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const filtered = recs.filter((r) => {
    const matchSearch = r.title.toLowerCase().includes(search.toLowerCase());
    const matchPaid = showPaidOnly ? r.visible_to_paid_only : true;
    return matchSearch && matchPaid;
  });

  return (
    <div className={styles.page}>
      <div className="container">
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Рекомендации по БАДам</h1>
          <p className={styles.pageSubtitle}>
            Проверенные рекомендации от специалистов клуба
          </p>
        </div>

        {/* Фильтры */}
        <div className={styles.filters}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Поиск по названию..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {isPaid && (
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={showPaidOnly}
                onChange={(e) => setShowPaidOnly(e.target.checked)}
              />
              Только Premium
            </label>
          )}
        </div>

        {isLoading && <p className={styles.loading}>Загрузка...</p>}
        {error && <p className={styles.error}>{error}</p>}

        {!isLoading && !error && filtered.length === 0 && (
          <div className={styles.empty}>
            <p>Рекомендаций пока нет{search ? ' по вашему запросу' : ''}.</p>
          </div>
        )}

        {!isLoading && !error && filtered.length > 0 && (
          <div className={styles.grid}>
            {filtered.map((rec) => (
              <RecommendationCard key={rec.id} rec={rec} isPaid={isPaid} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Recommendations;
